<template>
  <div class="whatsapp-template-config">
    <div class="config-row config-row--primary">
      <div class="field-group">
        <select
          :value="actionName"
          class="action__question w-full"
          @change="onActionChange"
        >
          <option
            v-for="actionType in actionTypes"
            :key="actionType.key"
            :value="actionType.key"
          >
            {{ actionType.label }}
          </option>
        </select>
      </div>

      <div class="field-group">
        <div class="multiselect-wrap--small whatsapp-template-multiselect">
          <multiselect
            v-model="selectedInbox"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.INBOX_PLACEHOLDER')"
            :options="inboxOptions"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            :max-height="180"
            @select="onInboxSelect"
          >
            <template #singleLabel="{ option }">
              <span v-tooltip="option.name" class="single-label-truncate">
                {{ option.name }}
              </span>
            </template>
            <template #option="{ option }">
              <span :title="option.name" class="option-truncate">
                {{ option.name }}
              </span>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="field-group">
        <div class="multiselect-wrap--small whatsapp-template-multiselect">
          <multiselect
            v-model="selectedMacro"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('AUTOMATION.ACTION.WHATSAPP_TEMPLATE.MACRO_PLACEHOLDER')"
            :options="macroOptions"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            :max-height="180"
            @select="onMacroSelect"
          >
            <template #singleLabel="{ option }">
              <span v-tooltip="option.name" class="single-label-truncate">
                {{ option.name }}
              </span>
            </template>
            <template #option="{ option }">
              <span :title="option.name" class="option-truncate">
                {{ option.name }}
              </span>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="field-group field-group--delay">
        <input
          :value="delayMinutes"
          type="number"
          min="0"
          :max="macroDelayBounds.MAX"
          class="answer--text-input macro-delay-input"
          placeholder="Delay (min)"
          title="Optional delay in minutes before sending WhatsApp template"
          @input="onDelayInput"
          @blur="$emit('touch-delay-validation')"
        />
      </div>

      <woot-button
        v-if="showRemove"
        icon="dismiss"
        variant="clear"
        color-scheme="secondary"
        class="remove-action-btn"
        @click="$emit('remove-action')"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { MACRO_DELAY_MINUTES } from 'dashboard/routes/dashboard/settings/automation/constants';

export default {
  components: { Multiselect },
  props: {
    value: {
      type: [Object, Array],
      default: () => ({}),
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    actionName: {
      type: String,
      default: '',
    },
    showRemove: {
      type: Boolean,
      default: true,
    },
    delayMinutes: {
      type: [Number, String],
      default: '',
    },
  },
  data() {
    return {
      macroDelayBounds: MACRO_DELAY_MINUTES,
      localConfig: {
        inbox_id: null,
        macro_id: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      whatsappInboxes: 'inboxes/getWhatsappInboxes',
      allMacros: 'macros/getMacros',
    }),
    inboxOptions() {
      return (this.whatsappInboxes || []).map(inbox => ({
        id: inbox.id,
        name: inbox.name,
      }));
    },
    macroOptions() {
      return (this.allMacros || [])
        .filter(macro => this.macroHasWhatsappTemplate(macro))
        .map(macro => ({ id: macro.id, name: macro.name }));
    },
    selectedInbox: {
      get() {
        return (
          this.inboxOptions.find(
            i => String(i.id) === String(this.localConfig.inbox_id)
          ) || null
        );
      },
      set() {
        // handled by @select
      },
    },
    selectedMacro: {
      get() {
        return (
          this.macroOptions.find(
            m => String(m.id) === String(this.localConfig.macro_id)
          ) || null
        );
      },
      set() {
        // handled by @select
      },
    },
  },
  watch: {
    value: {
      handler(newValue) {
        this.hydrateFromValue(newValue);
      },
      deep: true,
      immediate: true,
    },
    inboxOptions() {
      this.hydrateFromValue(this.value);
    },
    macroOptions() {
      this.hydrateFromValue(this.value);
    },
  },
  mounted() {
    this.emitUpdate();
  },
  methods: {
    onActionChange(event) {
      this.$emit('update:action-name', event.target.value);
    },
    macroHasWhatsappTemplate(macro) {
      if (!macro || !Array.isArray(macro.actions)) return false;
      return macro.actions.some(
        action => action.action_name === 'whatsapp_template'
      );
    },
    hydrateFromValue(value) {
      const source = Array.isArray(value) ? value[0] : value;
      if (source && typeof source === 'object') {
        this.localConfig = {
          inbox_id: source.inbox_id ?? null,
          macro_id: source.macro_id ?? null,
        };
      }
    },
    onInboxSelect(option) {
      this.localConfig.inbox_id = option?.id ?? null;
      this.emitUpdate();
    },
    onMacroSelect(option) {
      this.localConfig.macro_id = option?.id ?? null;
      this.emitUpdate();
    },
    onDelayInput(event) {
      this.$emit('update:delay-minutes', event.target.value);
    },
    emitUpdate() {
      this.$emit('input', { ...this.localConfig });
    },
  },
};
</script>

<style scoped lang="scss">
.whatsapp-template-config {
  @apply w-full;
}

.config-row--primary {
  display: grid;
  grid-template-columns:
    minmax(0, 1.1fr)
    minmax(0, 1fr)
    minmax(0, 1fr)
    6.5rem
    auto;
  gap: 0.75rem;
  align-items: center;
}

.field-group {
  @apply flex flex-col min-w-0 w-full;
}

.field-group--delay {
  @apply w-full min-w-[6.5rem];
}

.macro-delay-input {
  @apply w-full min-w-[6.5rem] flex-shrink-0 mb-0 h-10;
}

.action__question {
  @apply w-full max-w-full mb-0 mr-0;
}

.remove-action-btn {
  @apply shrink-0;
}

.whatsapp-template-multiselect {
  @apply w-full min-w-0;

  :deep(.multiselect) {
    @apply mb-0 h-10 min-h-[2.5rem];
  }

  :deep(.multiselect__tags) {
    @apply h-10 min-h-[2.5rem] max-h-10 flex items-center overflow-hidden;
  }

  :deep(.multiselect__select) {
    @apply h-10 min-h-[2.5rem];
  }

  :deep(.multiselect__single),
  :deep(.multiselect__placeholder) {
    @apply block w-full max-w-full whitespace-nowrap overflow-hidden text-ellipsis mb-0 py-1 px-0.5;
  }

  .single-label-truncate,
  .option-truncate {
    @apply block w-full whitespace-nowrap overflow-hidden text-ellipsis;
  }

  :deep(.multiselect__content-wrapper) {
    @apply w-full;
    overflow-x: hidden !important;
  }

  :deep(.multiselect__content) {
    @apply w-full max-w-full block;
  }

  :deep(.multiselect__option) {
    @apply block w-full whitespace-nowrap overflow-hidden text-ellipsis;

    > span {
      @apply block w-full whitespace-nowrap overflow-hidden text-ellipsis;
    }
  }
}
</style>
